import type { StackProps } from '@chakra-ui/react'
import { Stack } from '@chakra-ui/react'
import { useViewport } from '@xyflow/react'
import type { FC } from 'react'
import { memo } from 'react'

import { Card, CardDueDate } from '../components'
import CardLabels from '../components/cardLabels'

import useIsAnonymousUser from '@app/hooks/useIsAnonymousUser'
import WithLabel from '@app/next/forms/withLabel'
import CollapsedNodes from '@app/pages/strategies/components/nodes/components/collapsedNodes'
import { scaleSizeToMapZoom } from '@app/pages/strategies/components/nodes/helpers'
import SimImpactsSection from '@app/pages/strategies/components/nodes/simulator/simImpactsSection'
import cardFontSize from '@app/shared/cards/cardFontSize'
import cardMetadata from '@app/shared/cards/cardMetadata'
import CardDetailsLink from '@app/shared/cards/components/cardDetailsLink'
import CardGoalsCTA from '@app/shared/cards/components/cardGoalsCTA'
import CardSection from '@app/shared/cards/components/cardSection'
import CardSharedActions from '@app/shared/cards/components/cardSharedActions'
import CardTitle from '@app/shared/cards/components/cardTitle'
import CardTypeSection from '@app/shared/cards/components/cardTypeSection'
import CardEntityContributorsAndStatus, {
  shouldRenderCardEntityContributorsAndStatus
} from '@app/shared/cards/entity/cardEntityContributorsAndStatus'
import CardEntityStatsAndProgress, {
  shouldRenderCardEntityStatsAndProgress
} from '@app/shared/cards/entity/cardEntityStatsAndProgress'
import CardEntityStatus from '@app/shared/cards/entity/cardEntityStatus'
import MarkdownDisplay from '@app/shared/markdownDisplay'
import PulsingCircle from '@app/shared/pulsingCircle'
import type { DomainEntity, DomainNode, MapMode } from '@app/types'
import ICON_MAP, { COLOR_MAP } from '@app/utils/iconMap'

interface Props extends StackProps {
  strategyId: string
  entity?: DomainEntity
  node?: DomainNode
  onClick?: () => void
  onDoubleClick?: () => void
  mapMode?: MapMode
}

const EntityCard: FC<Props> = ({ strategyId, entity, node, mapMode = 'build', ...rest }) => {
  const isAnonymousUser = useIsAnonymousUser(strategyId)
  const { size, color } = cardMetadata(node)
  const simulationMode = mapMode === 'simulation'

  const { zoom } = useViewport()
  const {
    dueDate,
    foreignState,
    goalHypothesis,
    id,
    labels,
    linkTitle,
    linkUrl,
    recentActivity,
    results,
    showGoalHypothesis,
    showResults,
    showSummary,
    sourceName,
    summary,
    workflowState
  } = entity

  const route = isAnonymousUser ? null : `/strategy/${strategyId}/map/entity/${id}`

  const pulseSize = scaleSizeToMapZoom(24, zoom, 44)
  const fontSize = cardFontSize('sm', size)

  const showGoalsCTA = !isAnonymousUser && mapMode === 'goals' && !goalHypothesis

  return (
    <Card color={color} {...rest}>
      <input type="hidden" name="sourceName" value={sourceName || 'web'} />
      <CardTypeSection title="Work" icon={ICON_MAP.Work} color={COLOR_MAP.Entity} route={route} size={size} />
      <CardSection size={size}>
        <PulsingCircle isActive={!!recentActivity} size={pulseSize} link={`entity/${id}/events`} />
        <Stack gap={size === 'large' ? 6 : 2}>
          <CardTitle domainObject={entity} name="name" size={size} />
          {showSummary && summary && <MarkdownDisplay text={summary} />}
          {showGoalHypothesis && goalHypothesis && (
            <WithLabel showLabel={!!goalHypothesis} label="Goal / Hypothesis">
              <MarkdownDisplay text={goalHypothesis} fontSize={fontSize} />
            </WithLabel>
          )}
          {showResults && results && (
            <WithLabel showLabel={!!results} label="Results">
              <MarkdownDisplay text={results} />
            </WithLabel>
          )}
          {linkUrl && <CardDetailsLink url={linkUrl} text={linkTitle} size={size} />}
        </Stack>
      </CardSection>
      {showGoalsCTA && <CardGoalsCTA link={`entity/${id}/details`} size={size} />}
      {shouldRenderCardEntityStatsAndProgress(entity) && (
        <CardSection size={size}>
          <CardEntityStatsAndProgress entity={entity} size={size} />
        </CardSection>
      )}

      {shouldRenderCardEntityContributorsAndStatus(entity) && (
        <CardSection size={size}>
          <CardEntityContributorsAndStatus entity={entity} size={size} />
        </CardSection>
      )}
      {dueDate && (
        <CardSection size={size}>
          <CardDueDate domainObject={entity} fieldName="dueDate" size={size} />
        </CardSection>
      )}
      {labels?.length && <CardLabels fieldName="labels" domainObject={entity} size={size} />}
      <CardSharedActions domainObject={entity} size={size} route={route} includeConfidenceRating={false}>
        <CardEntityStatus status={foreignState} workflowState={workflowState} size={size} />
      </CardSharedActions>
      {node && <CollapsedNodes node={node} />}
      {simulationMode && <SimImpactsSection node={node} size={size} />}
    </Card>
  )
}

export default memo(EntityCard)
